import { baseApi } from './baseApi';
import { buildListQueryParams } from '../utils/pagination';
import { patchAllCachedQueryData } from './queryCacheUtils';

const toAccountFormData = (account = {}) => {
  const fd = new FormData();
  Object.entries(account).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (
      value === '' &&
      key !== 'employment_nature' &&
      key !== 'cost_centre_type'
    ) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null || item === '') return;
        fd.append(`account[${key}][]`, item);
      });
      return;
    }

    if (typeof value === 'boolean') {
      fd.append(`account[${key}]`, value ? 'true' : 'false');
      return;
    }

    fd.append(`account[${key}]`, value);
  });
  return fd;
};

export const employeeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getActiveDepartments: builder.query({
      query: () => '/api/accountable_departments',
      keepUnusedDataFor: 300,
    }),
    getActiveDesignations: builder.query({
      /**
       * Accepts one department id, or an array of ids.
       * When multiple departments are selected, fetches roles for each and merges uniquely.
       */
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const ids = (
          Array.isArray(arg) ? arg : arg == null || arg === '' ? [] : [arg]
        )
          .map((id) => String(id).trim())
          .filter(Boolean);

        const urls = ids.length
          ? ids.map(
              (id) =>
                `/api/roles/active_role_list?accountable_department_id=${encodeURIComponent(id)}`
            )
          : ['/api/roles/active_role_list'];

        const results = await Promise.all(urls.map((url) => baseQuery(url)));
        const failed = results.find((r) => r.error);
        if (failed?.error) return { error: failed.error };

        const byId = new Map();
        for (const result of results) {
          const payload = result?.data;
          const rows = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
          for (const row of rows) {
            const key = String(row?.id ?? row?.role_id ?? '');
            if (!key || byId.has(key)) continue;
            byId.set(key, row);
          }
        }

        return {
          data: {
            success: true,
            data: Array.from(byId.values()),
          },
        };
      },
    }),
    getLocationByReportingSupervisorComplaint: builder.query({
      query: (arg = {}) => {
        const locationId = arg.location_id ?? arg.locationId;
        const accountId = arg.account_id ?? arg.accountId;
        const params = new URLSearchParams();
        if (locationId != null && String(locationId).trim() !== '') {
          params.set('location_id', String(locationId));
        }
        if (accountId != null && String(accountId).trim() !== '') {
          params.set('account_id', String(accountId));
        }
        const qs = params.toString();
        return qs
          ? `/api/accounts/location_by_reporting_supervisor_complaint?${qs}`
          : '/api/accounts/location_by_reporting_supervisor_complaint';
      },
      transformResponse: (response) => {
        const normalizeRow = (row) => {
          if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
          const id =
            row.reporting_supervisor_id ??
            row.manager_on_duty_id ??
            row.id;
          if (id == null || id === '') return null;
          const name = String(
            row.reporting_supervisor_name ??
              row.name ??
              row.employee_name ??
              row.account_name ??
              ''
          ).trim();
          if (!name) return null;
          return { ...row, id, name };
        };

        const payload = response?.data;
        let rows = [];
        if (Array.isArray(payload)) {
          rows = payload;
        } else if (payload && typeof payload === 'object') {
          if (Array.isArray(payload.reporting_supervisors)) {
            rows = payload.reporting_supervisors;
          } else if (Array.isArray(payload.data)) {
            rows = payload.data;
          } else if (
            payload.reporting_supervisor_id != null ||
            payload.manager_on_duty_id != null ||
            (payload.id != null &&
              (payload.reporting_supervisor_name != null || payload.name != null))
          ) {
            rows = [payload];
          }
        }

        return {
          ...response,
          data: rows.map(normalizeRow).filter(Boolean),
        };
      },
    }),
    getActiveLocations: builder.query({
      query: () => '/api/locations/active_location_list',
    }),
    getLocationsByDepartment: builder.query({
      query: (arg) => {
        const accountableDepartmentId =
          arg && typeof arg === 'object'
            ? arg.accountable_department_id ?? arg.accountableDepartmentId ?? arg.departmentId
            : arg;
        const locationId =
          arg && typeof arg === 'object' ? arg.location_id ?? arg.locationId : undefined;

        const params = new URLSearchParams();
        if (accountableDepartmentId != null && String(accountableDepartmentId).trim() !== '') {
          params.set('accountable_department_id', String(accountableDepartmentId));
        }
        if (locationId != null && String(locationId).trim() !== '') {
          params.set('location_id', String(locationId));
        }

        const qs = params.toString();
        return qs
          ? `/api/locations/location_by_reporting_supervisor?${qs}`
          : '/api/locations/location_by_reporting_supervisor';
      },
      transformResponse: (response) => {
        const data = (response?.data || []).map((loc) => ({
          ...loc,
          id: loc.location_id ?? loc.id,
          name: loc.location_name ?? loc.name,
          reporting_supervisors: Array.isArray(loc.reporting_supervisors)
            ? loc.reporting_supervisors
            : [],
        }));
        return { ...response, data };
      },
    }),
    getEmployees: builder.query({
      query: (args = {}) => ({
        url: '/api/accounts',
        params: buildListQueryParams(args),
      }),
      providesTags: ['Employees'],
    }),
    getOutsourceAndProductionIndex: builder.query({
      query: (args = {}) => ({
        url: '/api/accounts/outsorce_and_production_index',
        params: buildListQueryParams(args),
      }),
      providesTags: ['Employees'],
    }),
    getEmployeeById: builder.query({
      query: (id) => `/api/accounts/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Employees', id }],
    }),
    createEmployee: builder.mutation({
      query: (account) => ({
        url: '/api/accounts',
        method: 'POST',
        body: toAccountFormData(account),
      }),
      invalidatesTags: ['Employees'],
    }),
    updateEmployee: builder.mutation({
      query: ({ id, account }) => ({
        url: `/api/accounts/${id}`,
        method: 'PUT',
        body: toAccountFormData(account),
      }),
      invalidatesTags: (_result, _error, { id }) => ['Employees', { type: 'Employees', id }],
    }),
    deleteEmployee: builder.mutation({
      query: (id) => ({
        url: `/api/accounts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => ['Employees', { type: 'Employees', id }],
    }),

    updateAccountStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/api/accounts/${id}/update_status`,
        method: 'PATCH',
        body: { status },
      }),
      async onQueryStarted({ id, status }, { dispatch, getState, queryFulfilled }) {
        const updateDraft = (draft) => {
          const list = draft?.data;
          if (!Array.isArray(list)) return;
          const row = list.find((a) => Number(a.id) === Number(id));
          if (row) row.status = status;
        };
        const patchResults = [
          ...patchAllCachedQueryData({
            dispatch,
            getState,
            endpointName: 'getEmployees',
            updateDraft,
          }),
          ...patchAllCachedQueryData({
            dispatch,
            getState,
            endpointName: 'getOutsourceAndProductionIndex',
            updateDraft,
          }),
        ];
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    updateProfileImage: builder.mutation({
      query: ({ id, employee_image }) => {
        const fd = new FormData();
        fd.append('employee_image', employee_image);
        return {
          url: `/api/accounts/${encodeURIComponent(String(id))}/update_profile_image`,
          method: 'PATCH',
          body: fd,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        'Employees',
        { type: 'Employees', id },
      ],
    }),

    getUpcomingBirthdays: builder.query({
      query: () => '/api/accounts/upcoming_birthdays',
    }),

    getUpcomingAnniversaries: builder.query({
      query: () => '/api/accounts/upcoming_anniversaries',
    }),
  }),
});

export const {
  useGetActiveDepartmentsQuery,
  useGetActiveDesignationsQuery,
  useGetLocationByReportingSupervisorComplaintQuery,
  useGetActiveLocationsQuery,
  useGetLocationsByDepartmentQuery,
  useGetEmployeesQuery,
  useGetOutsourceAndProductionIndexQuery,
  useGetEmployeeByIdQuery,
  useLazyGetEmployeeByIdQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useUpdateAccountStatusMutation,
  useUpdateProfileImageMutation,
  useDeleteEmployeeMutation,
  useGetUpcomingBirthdaysQuery,
  useGetUpcomingAnniversariesQuery,
} = employeeApi;

